import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Users, UserPlus, UserCheck, PieChart, TrendingUp, Calendar } from 'lucide-react';
import { dashboardAPI } from '../../../services/dashboardService';
import StatCard from './ui/StatCard';
import SectionCard from './ui/SectionCard';
import ProgressBar from './ui/ProgressBar';
import DataTable from './ui/DataTable';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';

const UsersTab = ({ filters, onViewDetailed }) => {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const USER_TYPE_CATEGORIES = useMemo(() => [
        { value: 'PET_GUARDIAN', label: t('adminDashboard.userTypes.petGuardian'), color: 'bg-purple-500', fill: '#a855f7' },
        { value: 'VETERINARIAN', label: t('adminDashboard.userTypes.veterinarian'), color: 'bg-cyan-500', fill: '#06b6d4' },
        { value: 'SERVICE_PROVIDER', label: t('adminDashboard.userTypes.serviceProvider'), color: 'bg-orange-500', fill: '#f97316' },
        { value: 'FIRM', label: t('adminDashboard.userTypes.firm'), color: 'bg-[#1B3A6B]', fill: '#1B3A6B' }
    ], [t]);

    const RECENT_USER_COLUMNS = [
        { key: 'name', header: t('systemDashboard.users.columns.name') },
        { key: 'email', header: t('systemDashboard.users.columns.email') },
        { key: 'type', header: t('systemDashboard.users.columns.type') },
        {
            key: 'created_at',
            header: t('systemDashboard.users.columns.registered'),
            width: '140px',
            render: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString() : t('systemDashboard.common.na'))
        }
    ];

    const loadUserStats = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await dashboardAPI.getUserStats(filters);
            setStats(response.data);
        } catch (err) {
            console.error('Failed to load user stats:', err);
            setError(err.message || t('systemDashboard.users.loadError'));
        } finally {
            setLoading(false);
        }
    }, [filters, t]);

    useEffect(() => {
        loadUserStats();
    }, [loadUserStats]);

    const userTypeData = useMemo(() => {
        return USER_TYPE_CATEGORIES.map((category) => ({
            name: category.label,
            value: stats?.usersByType?.[category.value] || 0,
            color: category.fill
        }));
    }, [USER_TYPE_CATEGORIES, stats?.usersByType]);

    const breakdownTotal = useMemo(() =>
        USER_TYPE_CATEGORIES.reduce(
            (sum, category) => sum + (stats?.usersByType?.[category.value] || 0),
            0
        ),
    [USER_TYPE_CATEGORIES, stats?.usersByType]);

    const graphData = stats?.graph_data || [];

    if (error) {
        return (
            <div className="error-container">
                <p>{error}</p>
                <button onClick={loadUserStats} className="retry-button">{t('systemDashboard.common.retry')}</button>
            </div>
        );
    }

    return (
        <div className="sd-container">
            <div className="sd-detailed-link-row">
                <button
                    onClick={onViewDetailed}
                    className="sd-detailed-link"
                >
                    {t('systemDashboard.common.detailed')} <ArrowRight size={14} />
                </button>
            </div>

            {/* KPI Cards */}
            <div className="sd-grid sd-grid--kpi-users">
                <StatCard
                    icon={Users}
                    label={t('systemDashboard.users.totalUsers')}
                    value={stats?.totalUsers || 0}
                    trend={stats?.userGrowth}
                    trendLabel=""
                    color="bg-blue-500"
                    loading={loading}
                />
                <StatCard
                    icon={UserCheck}
                    label={t('systemDashboard.users.activeUsers')}
                    value={stats?.activeUsers || 0}
                    subtitle={t('systemDashboard.users.ofTotal', { percent: (stats?.activePercentage || 0).toFixed(1) })}
                    color="bg-emerald-500"
                    loading={loading}
                />
                <StatCard
                    icon={UserPlus}
                    label={t('systemDashboard.users.newUsers')}
                    value={stats?.newUsers || 0}
                    subtitle={t('systemDashboard.users.inRange')}
                    color="bg-violet-500"
                    loading={loading}
                />
            </div>

            {/* Charts */}
            {!loading && (
                <div className="sd-grid sd-grid--charts">
                    <SectionCard title={t('systemDashboard.users.userDistribution')} icon={PieChart}>
                        <CustomDonutChart
                            data={userTypeData}
                            totalValue={stats?.totalUsers || 0}
                            title=""
                        />
                    </SectionCard>
                    <SectionCard title={t('systemDashboard.users.registrationGrowth')} icon={TrendingUp} className="sd-card--wide">
                        <CustomAreaChart data={graphData} color="#3b82f6" title="" />
                    </SectionCard>
                </div>
            )}

            {/* User Type Breakdown + Recent Registrations */}
            {!loading && (
                <div className="sd-grid sd-grid--split sd-grid--last">
                    <SectionCard title={t('systemDashboard.users.userTypeBreakdown')} icon={Users}>
                        <div className="sd-progress-bar-list">
                            {USER_TYPE_CATEGORIES.map((category) => {
                                const count = stats?.usersByType?.[category.value] || 0;
                                return (
                                    <ProgressBar
                                        key={category.value}
                                        label={category.label}
                                        count={count}
                                        total={breakdownTotal || 1}
                                        color={category.color}
                                    />
                                );
                            })}
                        </div>
                    </SectionCard>

                    <SectionCard title={t('systemDashboard.users.recentRegistrations')} icon={Calendar}>
                        <DataTable
                            columns={RECENT_USER_COLUMNS}
                            data={stats?.recentUsers || []}
                            emptyText={t('systemDashboard.users.noRecentRegistrations')}
                        />
                    </SectionCard>
                </div>
            )}
        </div>
    );
};

export default UsersTab;
